import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function Restore() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>(".restore-reveal").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 1.1,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 85%" },
          }
        );
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="restore"
      ref={ref}
      className="relative bg-background-50 text-foreground-900 py-28 md:py-40 px-6 md:px-10"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 restore-reveal">
          <span className="w-8 h-px bg-foreground-900/40" />
          <span className="eyebrow text-foreground-700">05 — Restore</span>
        </div>

        <div className="grid md:grid-cols-12 gap-10 md:gap-16 mt-12 items-end">
          <h2 className="restore-reveal md:col-span-8 font-display font-light text-3xl md:text-[42px] leading-[1.08] tracking-tight text-foreground-950">
            Quiet is a treatment.
          </h2>
          <p className="restore-reveal md:col-span-4 text-foreground-700 text-[15px] font-light leading-relaxed max-w-md">
            Thermal waters, glacial pools, cedar steam — a slower rhythm, shaped by the mountain.
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-6 md:gap-8 mt-12 md:mt-14">
          <div className="restore-reveal md:col-span-8 h-[420px] md:h-[560px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Serene%20luxury%20alpine%20spa%20with%20long%20indoor%20thermal%20pool%20lined%20with%20natural%20stone%2C%20panoramic%20floor%20to%20ceiling%20window%20framing%20misty%20snow%20forest%2C%20soft%20natural%20diffused%20light%2C%20one%20swimmer%20small%20in%20frame%2C%20editorial%20wellness%20photography%2C%20quiet%20luxury%2C%20muted%20cool%20palette%20with%20warm%20stone%20accents%2C%20fine%20art%20minimalist%20composition&width=2000&height=1400&seq=restore-spa-01&orientation=landscape"
              alt="Thermal pool"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="md:col-span-4 flex flex-col gap-6">
            <div className="restore-reveal h-[200px] md:h-[270px] overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Architectural%20detail%20of%20a%20contemporary%20cedar%20sauna%20interior%2C%20warm%20amber%20light%20raking%20across%20vertical%20cedar%20slats%2C%20faint%20drift%20of%20steam%2C%20refined%20material%20texture%20close-up%2C%20no%20people%20no%20towels%20no%20candles%2C%20editorial%20architectural%20photography%2C%20quiet%20alpine%20wellness%2C%20muted%20warm%20wood%20tones%2C%20fine%20art%20minimalist%20composition&width=1000&height=1200&seq=restore-sauna-02&orientation=portrait&nocache=true"
                alt="Cedar sauna"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="restore-reveal h-[200px] md:h-[270px] overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Close-up%20of%20a%20natural%20stone%20pool%20edge%20meeting%20still%20thermal%20water%20with%20subtle%20ripples%20and%20drifting%20mist%2C%20reflected%20muted%20alpine%20mountain%20light%20on%20the%20surface%2C%20no%20leaves%20no%20candles%20no%20towels%2C%20cinematic%20editorial%20architectural%20photography%2C%20quiet%20luxury%20alpine%20wellness%2C%20muted%20cool%20palette%20with%20warm%20stone%2C%20fine%20art%20minimalist%20composition&width=1000&height=1200&seq=restore-still-02&orientation=portrait&nocache=true"
                alt="Still water"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        <div className="restore-reveal grid md:grid-cols-3 gap-10 md:gap-16 mt-20 border-t border-foreground-900/15 pt-10">
          {[
            { k: "Thermal", v: "38°C mineral pools sourced from the ridge spring." },
            { k: "Ritual", v: "90-minute Nordic cycle — heat, cold, silence." },
            { k: "Studio", v: "Sunrise breathwork above the tree line." },
          ].map((f) => (
            <div key={f.k}>
              <div className="eyebrow text-foreground-700">{f.k}</div>
              <p className="mt-3 font-display text-lg md:text-xl text-foreground-950 leading-snug font-light">
                {f.v}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}